import { ArrowRightIcon, Button, Header, NicknameIcon, Paragraph, TextInput } from "@datagame/ui";
import { ChangeEvent, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import classNames from "classnames/bind";

import { IS_NICKNAME_TAKEN } from "../../../graphql/queries/Game";
import { LeaderboardConfiguration } from "../../../gql/graphql";

import styles from "./NicknameModal.module.scss";

const cx = classNames.bind(styles);

export interface NicknameModalProps {
  isOpen: boolean;
  gameId: string;
  themeColor?: string;
  configuration: LeaderboardConfiguration;
  onContinue: (nickname: string) => void;
  projectId?: string;
}

export const NicknameModal = (props: NicknameModalProps) => {
  const { isOpen, gameId, themeColor, configuration, onContinue, projectId } = props;
  const { t } = useTranslation("leaderboard");
  const isDesktop = useMediaQuery({ query: "(min-width: 1280px)" });
  const [nickname, setNickname] = useState("");
  const [error, setError] = useState<string | undefined>();
  const { nicknameQuestion, nicknameQuestionColor, nicknameLabel } = configuration;

  const { refetch } = useQuery(IS_NICKNAME_TAKEN, {
    variables: {
      nickname,
      gameId,
    },
    skip: true,
  });

  const handleContinue = async () => {
    if (!nickname) {
      setError(t("nicknameModal.input.errors.empty") as string);
      return;
    }

    const {
      data: { gameIsNicknameTaken },
    } = await refetch();

    if (gameIsNicknameTaken) {
      setError(t("nicknameModal.input.errors.taken", { nickname }) as string);
      return;
    }

    onContinue(nickname);
  };

  const handleNicknameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setNickname(e.target.value);
    setError(undefined);
  };

  return (
    <div className={cx("portal", { visible: isOpen })}>
      <div className={styles.background} />
      <form
        className={styles.nicknameModal}
        onSubmit={(e) => {
          e.preventDefault();
          handleContinue();
        }}
      >
        <Header style={{ color: nicknameQuestionColor }} className={styles.header} variant="h6">
          {nicknameQuestion}
        </Header>
        <Paragraph color="secondary" size="small" className={styles.info}>
          {projectId ? t("nicknameModal.projectLeaderboardInfo") : t("nicknameModal.info")}
        </Paragraph>
        <Header className={styles.label} variant="h6" color="neutrals-600">
          {nicknameLabel}
        </Header>
        <TextInput
          className={styles.input}
          value={nickname}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={true}
          customColor={themeColor}
          onChange={handleNicknameChange}
          size={isDesktop ? "medium" : "small"}
          icon={<NicknameIcon />}
          error={error}
        />
        {error && (
          <Paragraph size="xs" className={styles.error}>
            {error}
          </Paragraph>
        )}
        <Button
          type="submit"
          customColor={themeColor}
          className={styles.submit}
          icon={<ArrowRightIcon />}
          iconSide="left"
        >
          {t("common:continue")}
        </Button>
      </form>
    </div>
  );
};
